import { Box, Container, Grid, TextField, Typography } from "@mui/material"
import { useApi } from "auth/useApi3"
import Button, { StandardButton } from "components/Button"
import { PageTabs } from "components/navigation/PageTabs/PageTabs"
import PaperBox from "components/PaperBox"
import { Formik } from "formik"
import { apiEndpoints } from "generated/apiEndpoints"
import { allRoutes } from "generated/routes"
import React from "react"
import { useTranslation } from "react-i18next"
import * as Yup from "yup"

const validationSchema = (t) =>
  Yup.object().shape({
    password: Yup.string().required(t("global.password_is_required", "Password is required")),
    passwordRepeat: Yup.string().test(
      "passwords-match",
      t("settings.user.the_password_must_match", "The passwords must match"),
      function (value) {
        return this.parent.password === value
      }
    )
  })

const PasswordResetPage = ({ match }) => {
  const [suggestedPassword, setSuggestedPassword] = React.useState("")
  const [suggestPasswordResult, suggestPasswordApi] = useApi()
  const [saveResult, saveApi] = useApi()
  const { t } = useTranslation()

  React.useEffect(() => {
    if (suggestPasswordResult.status === 2) setSuggestedPassword(suggestPasswordResult.data)
  }, [suggestPasswordResult])

  const handleSubmit = (values) => {
    saveApi.post(
      apiEndpoints.systemadmincustomers.passwordreset
        .replace("{id}", match.params.userId)
        .concat("?newPassword=")
        .concat(window.btoa(values.password))
    )
  }

  return (
    <div>
      <PageTabs
        routeBack
        tabs={[
          {
            path: allRoutes.SystemadminCustomersEditUsersEdit(match.params.id, match.params.userId),
            title: t("global.back", "Back")
          }
        ]}
      />
      <Grid container>
        <Grid item sm={3}></Grid>
        <Grid item sm={6}>
          <Container>
            <Box marginY={3}>
              <Grid item container direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h2">{t("settings.user.change_password", "Change password")}</Typography>
              </Grid>
            </Box>
            <PaperBox>
              <Box mb={2}>
                <Typography>
                  {t(
                    "settings.user.use_this_form_to_change_password",
                    "Use this form to change your password."
                  )}
                </Typography>
              </Box>
              <Box mb={2}>
                <Typography>
                  {t(
                    "settings.user.password_must_meet_requirements",
                    `The password must meet the requirements: Minimum 8 characters and maximum 64 characters long. If you want to get a suggestion for an approved password, click "Suggest password".`
                  )}
                </Typography>
              </Box>
              <Box mb={2}>
                <Box mb={2}>
                  <StandardButton
                    color="secondary"
                    onClick={() => {
                      suggestPasswordApi.get(apiEndpoints.util.getpasswordsuggestion)
                    }}>
                    {t("settings.user.suggest_password", "Suggest password")}
                  </StandardButton>
                </Box>
                <Box mb={2}>
                  <Typography>
                    {t("settings.user.password_suggestion", "Password suggestions")}: {suggestedPassword}{" "}
                  </Typography>
                </Box>
              </Box>
              <Box mb={2}>
                <Formik
                  onSubmit={handleSubmit}
                  initialValues={{
                    password: "",
                    passwordRepeat: ""
                  }}
                  enableReinitialize={true}
                  validateOnChange={false}
                  validationSchema={validationSchema(t)}>
                  {({
                    values,
                    errors,
                    touched,
                    handleChange,
                    handleBlur,
                    isSubmitting,
                    handleSubmit,
                    setFieldValue
                  }) => (
                    <form onSubmit={handleSubmit} noValidate autoComplete="off">
                      <Grid container>
                        <Grid item xs={12}>
                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <TextField
                                autoComplete="off"
                                fullWidth
                                id="password"
                                type="password"
                                label={t("settings.user.new_password", "New password")}
                                value={values.password}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={touched.password && errors.password ? true : false}
                                helperText={touched.password && errors.password ? errors.password : null}
                                variant="outlined"
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <TextField
                                autoComplete="off"
                                fullWidth
                                id="passwordRepeat"
                                type="password"
                                label={t("settings.user.repeat_password", "Repeat password")}
                                value={values.passwordRepeat}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={touched.passwordRepeat && errors.passwordRepeat ? true : false}
                                helperText={
                                  touched.passwordRepeat && errors.passwordRepeat
                                    ? errors.passwordRepeat
                                    : null
                                }
                                variant="outlined"
                              />
                            </Grid>
                            <Grid
                              container
                              direction="row"
                              justifyContent="flex-end"
                              alignItems="flex-end"
                              style={{ marginTop: "2rem" }}>
                              <Button
                                color="primary"
                                type="submit"
                                feedback={{
                                  errorTitle: t(
                                    "settings.user.could_not_update_your_password",
                                    "Could not update your password"
                                  )
                                }}
                                result={saveResult}>
                                {t("global.save", "Save")}
                              </Button>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </form>
                  )}
                </Formik>
              </Box>
              <Box>
                <Typography>
                  {t(
                    "settings.user.change_password_by_clicking_save",
                    `Change your password by clicking "Save". Don't forget to write down your password before you leave the page!`
                  )}
                </Typography>
              </Box>
            </PaperBox>
          </Container>
        </Grid>
        <Grid item sm={3}></Grid>
      </Grid>
    </div>
  )
}
export default PasswordResetPage
